import {
  LayoutDashboard,
  Sparkles,
  FolderKanban,
  Images,
  TrendingUp,
  Rss,
  Link2,
  MessagesSquare,
  BarChart3,
  Settings,
  type LucideIcon,
} from 'lucide-react';

/**
 * Primary navigation IA for the agency app (matches the Figma left pane). One list for every width:
 * the sidebar (`lg` up), the tablet rail (`md` to `lg`), and the bottom bar and its More sheet (below
 * `md`) all render from it, so a label, icon or route cannot differ between them.
 */
export interface NavItem {
  /** Where the link goes. May carry a query string when it opens one view of a page. */
  to: string;
  label: string;
  icon: LucideIcon;
  /** Match the route exactly (used for the index route). */
  end?: boolean;
  /** Below `md`: `bar` is a tab of the bottom bar, `more` a tile of the More sheet. */
  mobileSlot: 'bar' | 'more';
  /** A shorter label for the rail and the bottom bar, which are narrow; `label` stays its accessible name. */
  short?: string;
  /** The count it shows as a badge, if any (useNavBadges). */
  badge?: keyof NavBadges;
}

/** The counts on nav items: unread conversations, and Digest Sources whose pulls are failing. */
export interface NavBadges {
  messages: number;
  sources: number;
}

/** Top group — the main workflow. (New Content is the button above it, at the top of the sidebar.) */
export const mainNav: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, mobileSlot: 'bar' },
  { to: '/trends', label: 'Trends', icon: TrendingUp, mobileSlot: 'more' },
  { to: '/strategist', label: 'Content Strategist', icon: Sparkles, short: 'Strategist', mobileSlot: 'more' },
  { to: '/campaigns', label: 'Campaigns & Posts', icon: FolderKanban, short: 'Posts', mobileSlot: 'bar' },
  { to: '/media', label: 'Media Library', icon: Images, short: 'Media', mobileSlot: 'more' },
  { to: '/messages', label: 'Messages', icon: MessagesSquare, badge: 'messages', mobileSlot: 'bar' },
  { to: '/analytics', label: 'Analytics', icon: BarChart3, mobileSlot: 'more' },
];

/**
 * Section header for the bottom utility group (rendered above its items), and the name of its `nav`
 * landmark and of its group in the phone's More sheet. "Configurations" since the Brand Workspace
 * overhaul (spec "App-wide changes"); it was "Configure Sources". Its items did not change.
 */
export const utilityNavLabel = 'Configurations';

/** Bottom group — source configuration (pinned to the bottom). */
export const utilityNav: NavItem[] = [
  // The workspace's posting schedule. It is a view of Campaigns & Posts (`?view=scheduler`), linked
  // here so it is one click from anywhere rather than only from that page's header.
  {
    to: '/campaigns?view=scheduler',
    label: 'Scheduler Settings',
    icon: Settings,
    short: 'Scheduler',
    mobileSlot: 'more',
  },
  { to: '/sources', label: 'Digest Sources', icon: Rss, short: 'Digest', badge: 'sources', mobileSlot: 'more' },
  { to: '/connections', label: 'Connections', icon: Link2, mobileSlot: 'more' },
];

/** Every row, in sidebar order: what `activeNavTo` picks the current page from. */
export const allNav: NavItem[] = [...mainNav, ...utilityNav];

/** The bottom bar's page tabs, in sidebar order (New Content and More sit between and after them). */
export const barNav: NavItem[] = allNav.filter((item) => item.mobileSlot === 'bar');

/** The More sheet's two groups, each in sidebar order. */
export const moreNavGroups: { label: string; items: NavItem[] }[] = [
  { label: 'Workspace', items: mainNav.filter((item) => item.mobileSlot === 'more') },
  { label: utilityNavLabel, items: utilityNav.filter((item) => item.mobileSlot === 'more') },
];

/**
 * Which nav item is the current page, as its `to` — at most one.
 *
 * A link to one view of a page (`/campaigns?view=scheduler`) is current only while that view is
 * showing, and then it wins over the link to the page itself (`/campaigns`); on every other view
 * the page's own link is current. `NavLink` cannot express this: it matches on the path alone, so
 * it would mark both links current at once.
 */
export function activeNavTo(items: NavItem[], pathname: string, search: string): string | null {
  // Compared the way the router matches routes: ignoring case and a trailing slash.
  const path = (pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname).toLowerCase();
  const here = new URLSearchParams(search);
  let best: { to: string; score: number } | null = null;
  for (const item of items) {
    const [itemPath, query = ''] = item.to.split('?');
    const wanted = [...new URLSearchParams(query)];
    // A view link names one exact page; a plain link also covers the pages nested under it.
    const nested = !item.end && wanted.length === 0 && path.startsWith(`${itemPath}/`);
    if (path !== itemPath && !nested) continue;
    if (!wanted.every(([key, value]) => here.get(key) === value)) continue;
    // The longer path wins, then the link that also names the view.
    const score = itemPath.length * 10 + wanted.length;
    if (!best || score > best.score) best = { to: item.to, score };
  }
  return best ? best.to : null;
}
